/**
 * Lightweight registry that lets forms with unsaved changes intercept
 * "navigation-like" actions (e.g. toggling sandbox mode or switching teams)
 * so the action only runs once the user confirms in the discard dialog.
 *
 * A trigger component calls `runGuardedAction(action)`. Any mounted form that
 * has registered a guard via `registerNavigationGuard` gets a chance to hold
 * the action and run it later. If no guard intercepts it, the action runs
 * immediately.
 */
type GuardHandler = (run: () => void) => boolean;

const handlers = new Set<GuardHandler>();

/**
 * Registers a guard handler. The handler receives the action to run and should
 * return `true` if it intercepted the action (it will run it later after user
 * confirmation), or `false` to let the action run immediately.
 *
 * @returns an unregister function to call on cleanup.
 */
export function registerNavigationGuard(handler: GuardHandler): () => void {
  handlers.add(handler);
  return (): void => {
    handlers.delete(handler);
  };
}

/**
 * Runs an action, giving registered guards the chance to intercept it (e.g. to
 * show an unsaved-changes confirmation). If no guard intercepts, the action
 * runs immediately.
 */
export function runGuardedAction(run: () => void): void {
  let intercepted = false;
  handlers.forEach((handler) => {
    if (!intercepted && handler(run)) intercepted = true; // a guard will run it later
  });
  if (!intercepted) run();
}
